<template>
    <div id="quiz-app">
        <quiz-start v-if="!started" @start="startQuiz()" />
        <template v-else-if="getQuestion">
            <div class="quiz__question-heading">
                <h3 :class="titleClass" class="quiz__question-heading__header">
                    {{ Array.isArray(getQuestion) ? getGroup.title : getQuestion.text }}
                </h3>
            </div>

            <keep-alive>
                <tiles-question 
                    v-if="getQuestion.type === 'grid'" 
                    :tiles="getQuestion.options"
                    @tile-click="(id, nextId) => nextStep(nextId, id)" 
                    @next="(id, nextId) => nextStep(nextId, id)"
                    @back="prevStep" :next="buttonCaptions.next" 
                    :previous="buttonCaptions.previous"
                    :selectedId="resolveSelected(getGroup.id, getQuestion.id)" 
                    :showP="answeredQuestions.length > 0 && !submitted" 
                />

                <options-question 
                    v-if="getQuestion.type === 'options'" 
                    :items="getQuestion.options" 
                    @back="prevStep"
                    :selectedId="resolveSelected(getGroup.id, getQuestion.id)"
                    @next="(id, nextId) => nextStep(nextId, id)" 
                    :next="buttonCaptions.next"
                    :previous="buttonCaptions.previous"
                />

                <dropdown-question 
                    v-if="
                        (Array.isArray(getQuestion) &&
                        getQuestion[0].type === 'dropdown') ||
                        getQuestion.type === 'dropdown'
                        " 
                    :items="Array.isArray(getQuestion) ? getQuestion : [getQuestion]" 
                    @back="prevStep"
                    @next="payload => dropdownNextStep(payload)" 
                    :selectedIds="resolveSelected(getGroup.id)"
                    :next="buttonCaptions.next" :previous="buttonCaptions.previous" />
            </keep-alive>
        </template>
    </div>
</template>

<script>
    import Vue from "vue";
    import axios from "axios";
    import {
        datalayerQuizStart,
        datalayerQuizQuestion,
        datalayerQuizEnd
    } from "../../../components/datalayer";

    import QuizStart from "./views/QuizStart.vue";
    import TilesQuestion from "./views/TilesQuestion.vue";
    import OptionsQuestion from "./views/OptionsQuestion.vue";
    import DropdownQuestion from "./views/DropdownQuestion.vue";
    import {
        isMobile
    } from "./mixins/is-mobile";

    /* TODO: Not keeping track of nextGroupIds, 
       so if person goes back he doesn't have to 
       end up in group he was supposed to (may cause 
       trouble in future use cases) */

    export default {
        name: "Quiz",
        components: {
            QuizStart,
            TilesQuestion,
            OptionsQuestion,
            DropdownQuestion,
        },
        mixins: [isMobile],

        props: {
            quizName: {
                type: String,
                required: true
            },
            lang: {
                type: String,
                default: "en-US"
            }
        },

        data: function () {
            return {
                currentStep: 1,
                questionsCount: 9,
                buttonCaptions: {},
                started: false,
                submitted: false,

                groups: [],
                nextGroupQueue: [],
                nextGroupId: 0,
                currentGroupOrder: 0,
                currentGroupStep: 0,

                answeredQuestions: [],
                selectedQuestions: {}
            };
        },

        computed: {
            getGroup: function () {
                if (this.nextGroupId) {
                    return this.findGroup(this.nextGroupId);
                }

                const length = this.answeredQuestions.length;
                if (length > 0) {
                    const answer = this.answeredQuestions[length - 1];
                    if (answer) {
                        const group = this.findGroup(answer.groupId);
                        this.currentGroupStep = group.questions.findIndex(
                            q => q.id === answer.questionId
                        );
                        return group;
                    }
                }
            },

            getQuestion: function () {
                if (!this.getGroup) return;

                if (this.getGroup.questionsMode === "all") {
                    return this.getGroup.questions;
                }

                return this.getGroup.questions[this.currentGroupStep];
            },

            titleClass: function () {
                const {
                    getQuestion
                } = this;
                if (!getQuestion) return false;
                return {
                    small: getQuestion.type === "options",
                    large: ["dropdown", "grid"].includes(getQuestion.type)
                };
            }
        },

        watch: {
            currentGroupOrder: function (val) {
                const nextGroup = this.resolveNextGroup(val);
                if (!nextGroup && !this.submitted) {
                    this.submitQuiz();
                }
            },

            currentStep: function (val) {
                datalayerQuizQuestion(val);
            }
        },

        methods: {
            startQuiz: function () {
                this.started = true;
                datalayerQuizStart();
                datalayerQuizQuestion(this.currentStep);
            },

            findGroup: function (id) {
                return this.groups.find(group => group.id === id);
            },

            resolveNextGroup: function (order) {
                const queued = this.nextGroupQueue.shift();

                if (queued) {
                    this.nextGroupId = queued;
                    return queued;
                } 

                const nextGroup = this.groups.find(group => group.order === order);
                this.nextGroupId = nextGroup ? nextGroup.id : 0;
                return nextGroup;
            },

            resolveSelected: function (groupId, questionId) {
                const group = this.selectedQuestions[groupId];
                if (group) {
                    const groupFull = this.findGroup(groupId);
                    if (groupFull && groupFull.questionsMode === "all") {
                        return group;
                    }

                    return group[questionId];
                }

                return;
            },

            fetchQuestions: async function () {
                try {
                    const {
                        data
                    } = await axios.get("/api/conditionalquiz/getdata", {
                        params: {
                            quizName: this.quizName,
                            lang: this.lang
                        }
                    });

                    if (data.texts) {
                        Vue.set(this.buttonCaptions, "next", data.texts.next);
                        Vue.set(this.buttonCaptions, "previous", data.texts.previous);
                    }

                    if (data.quiz) {
                        this.groups = data.quiz.groups;
                        this.currentGroupOrder = 1;
                    }
                } catch (e) {
                    console.log("Couldn't load initial data." + e);
                }
            },

            submitQuiz: async function () {
                this.submitted = true;

                const result = {
                    quizName: this.quizName,
                    answers: this.answeredQuestions.flatMap(answer => {
                        if (answer.questions) {
                            return Object.keys(answer.questions).map(questionId => ({
                                questionId: parseInt(questionId),
                                optionId: answer.questions[questionId].optionId
                            }));
                        }

                        return {
                            questionId: answer.questionId,
                            optionId: answer.optionId
                        };
                    })
                };

                try {
                    const {
                        data
                    } = await axios.post(
                        "/api/conditionalquiz/saveresults",
                        result
                    );

                    datalayerQuizEnd(this.urlParam(data, "percentage"));
                    window.location.replace(data);
                } catch (e) {
                    console.log("Couldn't submit answers." + e);
                }
            },

            nextStep: function (nextGroupId, id) {
                console.log(nextGroupId, id)
                if (this.currentStep > this.questionsCount) return;

                nextGroupId > 0 && this.nextGroupQueue.push(nextGroupId);

                this.answeredQuestions.push({
                    questionId: this.getQuestion.id,
                    optionId: id,
                    groupId: this.getGroup.id
                });

                if (nextGroupId === 0) {
                    this.submitQuiz();
                    return;
                }

                if (
                    this.getGroup &&
                    this.currentGroupStep + 1 < this.getGroup.questions.length
                ) {
                    this.currentGroupStep += 1;
                } else {
                    this.currentGroupOrder += 1;
                    this.currentGroupStep = 0;
                }

                if (this.currentStep == this.questionsCount) return;

                this.currentStep += 1;

                window.scrollTo(0, 0);
            },

            dropdownNextStep: function (payload) {
                if (this.currentStep > this.questionsCount) return;

                Object.keys(payload).map(questionId => {
                    const question = payload[questionId];
                    if (!question) return;

                    question.nextGroupId > 0 &&
                        this.nextGroupQueue.push(question.nextGroupId);
                });

                this.answeredQuestions.push({
                    groupId: this.getGroup.id,
                    questions: payload
                });

                this.currentGroupOrder += 1;
                this.currentGroupStep = 0;

                if (this.currentStep == this.questionsCount) return;

                this.currentStep += 1;

                window.scrollTo(0, 0);
            },

            prevStep: function () {
                const prevQ = this.answeredQuestions.pop();

                if (prevQ.questions) {
                    this.dropdownPrevStep(prevQ);
                    return;
                }

                Vue.set(this.selectedQuestions, prevQ.groupId, {
                    ...this.selectedQuestions[prevQ.groupId],
                    [prevQ.questionId]: prevQ.optionId
                });

                if (this.currentGroupStep === 0) {
                    this.currentGroupOrder -= 1;
                }

                if (this.currentStep > 0) {
                    this.nextGroupId = prevQ.groupId;
                    this.currentGroupStep = this.getGroup.questions.findIndex(
                        question => question.id === prevQ.questionId
                    );
                    this.nextGroupQueue.pop();

                    this.currentStep -= 1;
                }

                window.scrollTo(0, 0);
            },

            dropdownPrevStep: function (prevQ) {
                let questions = {};
                Object.keys(prevQ.questions).forEach(
                    q => (questions[q] = {
                        ...prevQ.questions[q]
                    })
                );
                Vue.set(this.selectedQuestions, prevQ.groupId, questions);

                if (this.currentGroupStep === 0) {
                    this.currentGroupOrder -= 1;
                }

                if (this.currentStep > 0) {
                    this.nextGroupId = prevQ.groupId;
                    this.currentGroupStep = 0;
                    this.nextGroupQueue.pop();

                    this.currentStep -= 1;
                }

                window.scrollTo(0, 0);
            },

            urlParam: function (url, name) {
                const results = new RegExp("[?&]" + name + "=([^&#]*)").exec(url);
                if (results == null) {
                    return null;
                } else {
                    return decodeURI(results[1]) || 0;
                }
            }
        },
        mounted: function () {
            this.fetchQuestions();
        }
    };

</script>
